import React, { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Reviews = () => {
  const admin = JSON.parse(localStorage.getItem("admin") || "{}");

  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 5.0,
    breakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    activeCount: 0,
    hiddenCount: 0,
    userToProviderCount: 0,
    providerToUserCount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [selectedRating, setSelectedRating] = useState("ALL"); // ALL | 5 | 4 | 3 | 2 | 1
  const [selectedType, setSelectedType] = useState("ALL"); // ALL | USER_TO_PROVIDER | PROVIDER_TO_USER
  const [selectedStatus, setSelectedStatus] = useState("ALL"); // ALL | ACTIVE | HIDDEN
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modals & Actions
  const [deleteModalItem, setDeleteModalItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [detailModalItem, setDetailModalItem] = useState(null);

  // Toast notification
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  // Fetch reviews from backend
  const fetchReviews = useCallback(
    async (isManual = false) => {
      if (isManual) setRefreshing(true);
      else setLoading(true);

      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const params = new URLSearchParams();
        if (selectedRating !== "ALL") params.append("rating", selectedRating);
        if (selectedType !== "ALL") params.append("type", selectedType);
        if (selectedStatus !== "ALL") params.append("status", selectedStatus);
        if (searchQuery.trim()) params.append("search", searchQuery.trim());
        params.append("page", currentPage);
        params.append("limit", 20);

        const res = await fetch(
          `${BASE_URL}admin/reviews?${params.toString()}`,
          { headers }
        );
        const json = await res.json();

        if (json.status === "success" && json.data) {
          setReviews(json.data.reviews || []);
          if (json.data.stats) setStats(json.data.stats);
          if (json.data.pagination) {
            setTotalPages(json.data.pagination.totalPages || 1);
            setTotalItems(json.data.pagination.totalItems || 0);
          }
        } else {
          showToast(json.message || "Failed to load reviews", "error");
        }
      } catch (err) {
        console.error("Error fetching reviews:", err);
        showToast("Network error loading reviews", "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedRating, selectedType, selectedStatus, searchQuery, currentPage]
  );

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Toggle review visibility (hide/show in app)
  const handleToggleStatus = async (review) => {
    setTogglingId(review.id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}admin/reviews/${review.id}/toggle-status`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const json = await res.json();

      if (json.status === "success") {
        showToast(
          `Review visibility updated: ${
            json.data.isActive ? "Visible on app" : "Hidden from app"
          }`,
          "success"
        );
        // Update local state smoothly
        setReviews((prev) =>
          prev.map((r) =>
            r.id === review.id ? { ...r, isActive: json.data.isActive } : r
          )
        );
        fetchReviews(true);
      } else {
        showToast(json.message || "Failed to update review status", "error");
      }
    } catch (err) {
      console.error("Toggle error:", err);
      showToast("Network error toggling status", "error");
    } finally {
      setTogglingId(null);
    }
  };

  // Delete review permanently
  const handleConfirmDelete = async () => {
    if (!deleteModalItem) return;
    setDeleting(true);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}admin/reviews/${deleteModalItem.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const json = await res.json();

      if (json.status === "success") {
        showToast("Review deleted successfully", "success");
        setDeleteModalItem(null);
        fetchReviews(true);
      } else {
        showToast(json.message || "Failed to delete review", "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Network error deleting review", "error");
    } finally {
      setDeleting(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!reviews.length) {
      showToast("No records available to export", "error");
      return;
    }

    const headers = [
      "ID",
      "Date",
      "Rating",
      "Reviewer Role",
      "Reviewer Name",
      "Reviewer Phone",
      "Reviewee Role",
      "Reviewee Name",
      "Reviewee Phone",
      "Service Name",
      "Feedback Comment",
      "Status",
      "Booking ID",
    ];

    const rows = reviews.map((r) => [
      r.id,
      new Date(r.createdAt).toLocaleString("en-IN"),
      r.rating,
      r.reviewerType,
      `"${r.reviewer?.name || "N/A"}"`,
      `"${r.reviewer?.phone || "N/A"}"`,
      r.revieweeType,
      `"${r.reviewee?.name || "N/A"}"`,
      `"${r.reviewee?.phone || "N/A"}"`,
      `"${r.booking?.service?.name || "N/A"}"`,
      `"${(r.feedback || "").replace(/"/g, '""')}"`,
      r.isActive ? "ACTIVE" : "HIDDEN",
      `"${r.bookingId || ""}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `inb_reviews_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("Reviews exported to CSV", "success");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Render Stars SVG
  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "text-[#F5C518] fill-current" : "text-stone-300"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        ))}
      </div>
    );
  };

  const fiveStarPct =
    stats.totalReviews > 0
      ? Math.round(((stats.breakdown[5] || 0) / stats.totalReviews) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#1C1917] flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Container with lg:ml-72 so it is NOT hidden under the fixed sidebar */}
      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Toast Notification Banner */}
        {toast && (
          <div
            className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center space-x-3 text-xs font-bold transition-all ${
              toast.type === "success"
                ? "bg-stone-950 text-emerald-400 border border-emerald-500/30"
                : toast.type === "error"
                ? "bg-stone-950 text-rose-400 border border-rose-500/30"
                : "bg-stone-950 text-amber-300 border border-amber-500/30"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-current"></span>
            <span>{toast.message}</span>
          </div>
        )}

        {/* Sticky Top Header */}
        <header className="bg-white border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-16 lg:pt-0 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">
                <span>Operations</span>
                <span>/</span>
                <span className="text-stone-900 font-bold">Feedback Hub</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-stone-900 flex items-center gap-3">
                <span>Ratings & Reviews Moderation</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <span>Trust & Quality</span>
                </span>
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Monitor customer reviews, buddy ratings, inspect feedback
                sentiment, and moderate inappropriate comments.
              </p>
            </div>

            <div className="flex items-center space-x-3 self-start xl:self-auto">
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center px-3.5 py-2 text-xs font-bold rounded-xl bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 hover:border-stone-400 transition-all shadow-xs"
                title="Export filtered records to CSV"
              >
                <svg
                  className="w-4 h-4 mr-2 text-stone-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Export CSV
              </button>

              <button
                onClick={() => fetchReviews(true)}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-2 text-xs font-bold rounded-xl bg-stone-900 text-white hover:bg-stone-800 transition-all shadow-xs disabled:opacity-50"
              >
                <svg
                  className={`w-4 h-4 mr-2 text-[#F8D346] ${
                    refreshing ? "animate-spin" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </header>

        {/* Content Body */}
        <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-6">
          {/* Top Overall Rating & Star Distribution Banner */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              {/* Left Score Block (4 cols) */}
              <div className="lg:col-span-4 lg:border-r border-stone-200 lg:pr-6 flex flex-col items-center justify-center text-center">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">
                  Overall Platform Quality
                </span>
                <div className="text-5xl font-extrabold text-stone-900 tracking-tight">
                  {stats.averageRating.toFixed(1)}
                  <span className="text-2xl text-stone-400 font-normal"> / 5.0</span>
                </div>
                <div className="mt-2.5">{renderStars(Math.round(stats.averageRating))}</div>
                <p className="text-xs text-stone-500 mt-2 font-medium">
                  Calculated across{" "}
                  <strong className="text-stone-800 font-bold">
                    {stats.totalReviews} total reviews
                  </strong>
                </p>
                <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  <span>{fiveStarPct}% 5-Star Satisfaction</span>
                </div>
              </div>

              {/* Right Distribution Breakdown (8 cols) */}
              <div className="lg:col-span-8 space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500 block mb-3">
                  Rating Breakdown & Star Distribution
                </span>
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = stats.breakdown[star] || 0;
                  const pct =
                    stats.totalReviews > 0
                      ? Math.round((count / stats.totalReviews) * 100)
                      : 0;

                  return (
                    <div
                      key={star}
                      onClick={() => {
                        setSelectedRating(
                          selectedRating === String(star) ? "ALL" : String(star)
                        );
                        setCurrentPage(1);
                      }}
                      className={`flex items-center space-x-3 p-1 rounded-lg cursor-pointer transition-colors group ${
                        selectedRating === String(star)
                          ? "bg-amber-50"
                          : "hover:bg-stone-50"
                      }`}
                      title={`Filter by ${star} Stars`}
                    >
                      <div className="w-12 text-xs font-bold text-stone-700 flex items-center space-x-1 shrink-0">
                        <span>{star}</span>
                        <svg
                          className="w-3.5 h-3.5 text-[#F5C518] fill-current"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                          />
                        </svg>
                      </div>

                      {/* Progress bar */}
                      <div className="flex-1 h-3 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            star >= 4
                              ? "bg-gradient-to-r from-[#F8D346] to-[#F5C518]"
                              : star === 3
                              ? "bg-amber-400"
                              : "bg-rose-400"
                          }`}
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>

                      <div className="w-20 text-right text-xs text-stone-500 font-mono">
                        <span className="font-semibold text-stone-800">
                          {count}
                        </span>{" "}
                        ({pct}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 4 KPI Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Total Reviews */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-amber-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Total Reviews
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {stats.totalReviews}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span className="font-semibold text-stone-800">
                    {stats.activeCount}
                  </span>
                  <span>currently visible on app</span>
                </div>
              </div>
            </div>

            {/* Card 2: 5-Star Reviews */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-emerald-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  5-Star Ratings
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {stats.breakdown[5] || 0}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span className="font-semibold text-emerald-700">
                    {fiveStarPct}%
                  </span>
                  <span>of all reviews</span>
                </div>
              </div>
            </div>

            {/* Card 3: User to Provider Reviews */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-blue-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Customer Ratings
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {stats.userToProviderCount}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span>Customer feedback for buddies</span>
                </div>
              </div>
            </div>

            {/* Card 4: Hidden / Moderated */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-rose-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Moderated / Hidden
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 text-rose-700 border border-rose-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {stats.hiddenCount}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span>Hidden from client applications</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Bar & Search */}
          <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Star Rating Buttons */}
              <div className="flex items-center space-x-1 p-1 bg-stone-100 rounded-lg overflow-x-auto text-xs font-semibold">
                <button
                  onClick={() => {
                    setSelectedRating("ALL");
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md transition-all whitespace-nowrap ${
                    selectedRating === "ALL"
                      ? "bg-white text-stone-900 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  All Stars ({stats.totalReviews})
                </button>
                {[5, 4, 3, 2, 1].map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setSelectedRating(String(s));
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1.5 rounded-md transition-all whitespace-nowrap flex items-center space-x-1 ${
                      selectedRating === String(s)
                        ? "bg-white text-stone-900 shadow-sm font-bold"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    <span>{s}★</span>
                    <span className="text-[10px] text-stone-400 font-normal">
                      ({stats.breakdown[s] || 0})
                    </span>
                  </button>
                ))}
              </div>

              {/* Search Bar */}
              <div className="relative flex-1 md:max-w-md">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search by user, buddy name, or feedback quote..."
                  className="w-full pl-9 pr-8 py-2 text-xs bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white text-stone-900"
                />
                <svg
                  className="w-4 h-4 absolute left-3 top-2.5 text-stone-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-2.5 text-stone-400 hover:text-stone-600"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Secondary Filter Row: Review Direction & Moderation Status */}
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-stone-100 text-xs">
              <div className="flex items-center space-x-1 text-stone-500 font-semibold">
                <span>Direction:</span>
                {["ALL", "USER_TO_PROVIDER", "PROVIDER_TO_USER"].map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      setSelectedType(t);
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 rounded-md text-[11px] transition-colors ${
                      selectedType === t
                        ? "bg-stone-900 text-white font-bold"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {t === "ALL"
                      ? "All Types"
                      : t === "USER_TO_PROVIDER"
                      ? "Customer to Buddy"
                      : "Buddy to Customer"}
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-1 text-stone-500 font-semibold ml-auto">
                <span>Status:</span>
                {["ALL", "ACTIVE", "HIDDEN"].map((st) => (
                  <button
                    key={st}
                    onClick={() => {
                      setSelectedStatus(st);
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1 rounded-md text-[11px] transition-colors ${
                      selectedStatus === st
                        ? "bg-stone-900 text-white font-bold"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {st === "ALL"
                      ? "All Status"
                      : st === "ACTIVE"
                      ? "Visible"
                      : "Hidden"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Reviews Table */}
          <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-stone-50/80 border-b border-stone-200 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                    <th className="py-3 px-4">Reviewer</th>
                    <th className="py-3 px-4">Reviewee</th>
                    <th className="py-3 px-4">Rating</th>
                    <th className="py-3 px-4">Feedback & Service</th>
                    <th className="py-3 px-4">Date & Status</th>
                    <th className="py-3 px-4 text-right">Moderation Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 text-xs">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-12 text-center text-stone-400"
                      >
                        <div className="inline-flex items-center space-x-2">
                          <svg
                            className="w-5 h-5 animate-spin text-stone-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          <span>Loading platform reviews...</span>
                        </div>
                      </td>
                    </tr>
                  ) : reviews.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <div className="max-w-sm mx-auto space-y-3">
                          <div className="w-12 h-12 mx-auto rounded-full bg-stone-100 text-stone-400 flex items-center justify-center">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </div>
                          <div className="text-sm font-semibold text-stone-800">
                            No reviews found
                          </div>
                          <p className="text-xs text-stone-500">
                            {searchQuery
                              ? `No reviews match "${searchQuery}". Try changing search keywords or resetting filters.`
                              : "No reviews match the selected filter combination."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    reviews.map((item) => {
                      const isUserReviewer = item.reviewerType === "USER";

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-stone-50/70 transition-colors"
                        >
                          {/* 1. Reviewer */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-stone-800 text-[#F8D346] flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden">
                                {item.reviewer?.profileImage ? (
                                  <img
                                    src={item.reviewer.profileImage}
                                    alt="Reviewer"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  (item.reviewer?.name || "U")
                                    .charAt(0)
                                    .toUpperCase()
                                )}
                              </div>
                              <div>
                                <div className="font-semibold text-stone-900 flex items-center space-x-1.5">
                                  <span>{item.reviewer?.name || "Unknown"}</span>
                                  <span
                                    className={`inline-block px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider ${
                                      isUserReviewer
                                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                                        : "bg-amber-50 text-amber-800 border border-amber-200"
                                    }`}
                                  >
                                    {isUserReviewer ? "Customer" : "Buddy"}
                                  </span>
                                </div>
                                <div className="text-[11px] text-stone-500 font-mono mt-0.5">
                                  {item.reviewer?.phone || "No phone"}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* 2. Reviewee */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-stone-100 text-stone-700 flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden border border-stone-200">
                                {item.reviewee?.profileImage ? (
                                  <img
                                    src={item.reviewee.profileImage}
                                    alt="Reviewee"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  (item.reviewee?.name || "R")
                                    .charAt(0)
                                    .toUpperCase()
                                )}
                              </div>
                              <div>
                                <div className="font-semibold text-stone-900 flex items-center space-x-1.5">
                                  <span>{item.reviewee?.name || "Unknown"}</span>
                                  <span
                                    className={`inline-block px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider ${
                                      !isUserReviewer
                                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                                        : "bg-amber-50 text-amber-800 border border-amber-200"
                                    }`}
                                  >
                                    {!isUserReviewer ? "Customer" : "Buddy"}
                                  </span>
                                </div>
                                <div className="text-[11px] text-stone-500 font-mono mt-0.5">
                                  {item.reviewee?.phone || "No phone"}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* 3. Rating */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center space-x-2">
                              {renderStars(item.rating)}
                              <span className="font-bold text-stone-900 text-xs">
                                {item.rating}.0
                              </span>
                            </div>
                          </td>

                          {/* 4. Feedback & Service */}
                          <td className="py-3.5 px-4 max-w-xs">
                            <div className="text-xs text-stone-800 italic font-normal">
                              {item.feedback ? (
                                `"${item.feedback}"`
                              ) : (
                                <span className="text-stone-400 not-italic">
                                  No written comment
                                </span>
                              )}
                            </div>
                            {item.booking?.service && (
                              <div className="text-[11px] text-stone-500 mt-1 flex items-center space-x-1">
                                <span className="font-medium text-stone-700">
                                  {item.booking.service.name}
                                </span>
                                {item.booking.subService && (
                                  <>
                                    <span>•</span>
                                    <span>{item.booking.subService.name}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </td>

                          {/* 5. Date & Status */}
                          <td className="py-3.5 px-4">
                            <div className="text-stone-600 text-[11px]">
                              {formatDate(item.createdAt)}
                            </div>
                            <div className="mt-1">
                              {item.isActive ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                  <span className="w-1 h-1 rounded-full bg-emerald-500 mr-1.5"></span>
                                  Visible on App
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-800 border border-rose-200">
                                  <span className="w-1 h-1 rounded-full bg-rose-500 mr-1.5"></span>
                                  Hidden
                                </span>
                              )}
                            </div>
                          </td>

                          {/* 6. Moderation Actions */}
                          <td className="py-3.5 px-4 text-right">
                            <div className="inline-flex items-center space-x-1.5">
                              {/* Toggle Visibility */}
                              <button
                                onClick={() => handleToggleStatus(item)}
                                disabled={togglingId === item.id}
                                className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg border transition-all ${
                                  item.isActive
                                    ? "bg-white border-stone-300 text-stone-700 hover:bg-stone-50 hover:text-rose-600"
                                    : "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
                                }`}
                                title={
                                  item.isActive
                                    ? "Hide review from user apps"
                                    : "Unhide review"
                                }
                              >
                                {togglingId === item.id ? (
                                  <span className="animate-pulse">
                                    Updating...
                                  </span>
                                ) : item.isActive ? (
                                  "Hide Review"
                                ) : (
                                  "Make Visible"
                                )}
                              </button>

                              {/* Delete button */}
                              <button
                                onClick={() => setDeleteModalItem(item)}
                                className="p-1 text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                title="Delete review"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>

                              {/* Detail Modal button */}
                              <button
                                onClick={() => setDetailModalItem(item)}
                                className="p-1 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
                                title="Audit details"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-stone-200 bg-stone-50/50 flex items-center justify-between text-xs">
                <span className="text-stone-500">
                  Showing {(currentPage - 1) * 20 + 1} to{" "}
                  {Math.min(currentPage * 20, totalItems)} of {totalItems}{" "}
                  reviews
                </span>
                <div className="flex items-center space-x-1">
                  <button
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="px-2.5 py-1 rounded border border-stone-300 text-stone-700 bg-white hover:bg-stone-50 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 font-semibold text-stone-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    className="px-2.5 py-1 rounded border border-stone-300 text-stone-700 bg-white hover:bg-stone-50 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-stone-200">
            <div className="p-6 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-base font-bold text-stone-900">
                Delete Review Permanently?
              </h3>
              <p className="text-xs text-stone-500 leading-relaxed">
                Are you sure you want to permanently delete this {deleteModalItem.rating}
                -star review from{" "}
                <strong>{deleteModalItem.reviewer?.name || "Reviewer"}</strong>?
                This action cannot be undone.
              </p>
              {deleteModalItem.feedback && (
                <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 text-xs italic text-stone-700 text-left">
                  "{deleteModalItem.feedback}"
                </div>
              )}
            </div>
            <div className="px-6 py-3.5 bg-stone-50 border-t border-stone-200 flex items-center justify-end space-x-2">
              <button
                onClick={() => setDeleteModalItem(null)}
                className="px-4 py-2 text-xs font-medium rounded-lg text-stone-700 bg-white border border-stone-300 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 transition-all"
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AUDIT / DETAIL MODAL */}
      {detailModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-stone-200">
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/70 flex items-center justify-between">
              <h3 className="text-base font-bold text-stone-900">
                Review Audit Information
              </h3>
              <button
                onClick={() => setDetailModalItem(null)}
                className="text-stone-400 hover:text-stone-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-3 text-xs">
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Review ID:</span>
                <span className="font-mono text-stone-800">
                  {detailModalItem.id}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Rating Score:</span>
                <span className="font-bold text-stone-900">
                  {detailModalItem.rating} / 5 Stars
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Reviewer:</span>
                <span className="font-semibold text-stone-900">
                  {detailModalItem.reviewer?.name} ({detailModalItem.reviewerType})
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Reviewee:</span>
                <span className="font-semibold text-stone-900">
                  {detailModalItem.reviewee?.name} ({detailModalItem.revieweeType})
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Booking ID:</span>
                <span className="font-mono text-stone-800">
                  {detailModalItem.bookingId || "N/A"}
                </span>
              </div>
              {detailModalItem.booking?.service && (
                <div className="flex justify-between py-1.5 border-b border-stone-100">
                  <span className="text-stone-500">Service:</span>
                  <span className="font-medium text-stone-900">
                    {detailModalItem.booking.service.name} (
                    {detailModalItem.booking.subService?.name})
                  </span>
                </div>
              )}
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Created Date:</span>
                <span className="text-stone-800">
                  {formatDate(detailModalItem.createdAt)}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">App Visibility:</span>
                <span
                  className={`font-semibold ${
                    detailModalItem.isActive
                      ? "text-emerald-700"
                      : "text-rose-700"
                  }`}
                >
                  {detailModalItem.isActive ? "Visible" : "Hidden from App"}
                </span>
              </div>
              <div className="pt-2">
                <span className="text-stone-500 block mb-1">Feedback Quote:</span>
                <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 text-stone-800 italic">
                  {detailModalItem.feedback
                    ? `"${detailModalItem.feedback}"`
                    : "No written feedback provided."}
                </div>
              </div>
            </div>

            <div className="px-6 py-3.5 bg-stone-50 border-t border-stone-200 flex justify-end">
              <button
                onClick={() => setDetailModalItem(null)}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-stone-900 hover:bg-stone-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
